import React, { useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../config";

const DDS_API = `${API_BASE_URL}/dds/api/admin`;

function getToken() {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.token || "";
    } catch { return ""; }
}

function authHeaders() {
    return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` };
}

async function api(method, path, body) {
    const r = await fetch(`${DDS_API}${path}`, {
        method,
        headers: authHeaders(),
        body: body ? JSON.stringify(body) : undefined,
    });
    if (r.status === 204) return null;
    if (!r.ok) {
        const err = new Error(`${r.status} ${r.statusText}`);
        err.status = r.status;
        throw err;
    }
    return r.json();
}

// createdAt comes back as an ISO instant; always show it in ICT regardless of the browser's zone.
function fmtIct(v) {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("th-TH", {
        timeZone: "Asia/Bangkok", day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
    }) + " น.";
}

export default function DdsAdmin() {
    const [screens, setScreens] = useState([]);
    const [contents, setContents] = useState([]);
    const [tokens, setTokens] = useState({});           // screenId → [display token]
    const [adminTokens, setAdminTokens] = useState({}); // screenId → [admin token]
    const [assignments, setAssignments] = useState({}); // screenId → [assignment]
    const [newScreenName, setNewScreenName] = useState("");
    const [newToken, setNewToken] = useState(null);     // {screenId, token, type}
    const [mediaEvents, setMediaEvents] = useState({}); // screenId → {url, ttlSeconds}
    const [idleConfigs, setIdleConfigs] = useState({}); // screenId → {idleMinutes, idleShowMinutes, idleImageUrl, idleCooldownMinutes}
    const [moneyCardInputs, setMoneyCardInputs] = useState({}); // screenId → text being typed (absent = show saved value)
    const [revealed, setRevealed] = useState({});       // "display:<id>" | "admin:<id>" → {token} | {error}
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const DDS_DISPLAY_BASE = "https://dds.icecys.com";

    const load = useCallback(async () => {
        setError("");
        setLoading(true);
        try {
            const [sc, co] = await Promise.all([api("GET", "/screens"), api("GET", "/contents")]);
            setScreens(sc || []);
            setIdleConfigs(prev => {
                const next = { ...prev };
                for (const s2 of sc || []) {
                    if (!(s2.id in next)) {
                        next[s2.id] = {
                            idleMinutes: s2.idleMinutes ?? "",
                            idleShowMinutes: s2.idleShowMinutes ?? "",
                            idleImageUrl: s2.idleImageUrl ?? "",
                            idleCooldownMinutes: s2.idleCooldownMinutes ?? "",
                        };
                    }
                }
                return next;
            });
            setContents(co || []);
            const tMap = {}, atMap = {}, aMap = {};
            for (const s of sc || []) {
                const [tList, atList, aList] = await Promise.all([
                    api("GET", `/tokens?screenId=${s.id}`),
                    api("GET", `/admin-tokens?screenId=${s.id}`),
                    api("GET", `/assignments?screenId=${s.id}`),
                ]);
                tMap[s.id]  = tList  || [];
                atMap[s.id] = atList || [];
                aMap[s.id]  = aList  || [];
            }
            setTokens(tMap);
            setAdminTokens(atMap);
            setAssignments(aMap);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function createScreen() {
        if (!newScreenName.trim()) return;
        try {
            await api("POST", "/screens", { name: newScreenName.trim() });
            setNewScreenName("");
            await load();
        } catch (e) { setError(e.message); }
    }

    async function deleteScreen(id) {
        if (!window.confirm("ลบ screen นี้?")) return;
        try { await api("DELETE", `/screens/${id}`); await load(); }
        catch (e) { setError(e.message); }
    }

    async function createToken(screenId) {
        try {
            const res = await api("POST", "/tokens", { screenId, label: `จอ ${screenId}` });
            setNewToken({ screenId, token: res.token, type: "display" });
            await load();
        } catch (e) { setError(e.message); }
    }

    async function revokeToken(tokenId) {
        if (!window.confirm("ยกเลิก token นี้?")) return;
        try { await api("POST", `/tokens/${tokenId}/revoke`); await load(); }
        catch (e) { setError(e.message); }
    }

    // kind = "display" | "admin" — both have GET .../{id}/view. 410 = created before view-again
    // support (plaintext gone for good); anything else (403, 5xx, network) is reported as it is.
    async function toggleReveal(kind, tokenId) {
        const key = `${kind}:${tokenId}`;
        if (revealed[key] !== undefined) {
            setRevealed(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            return;
        }
        const path = kind === "admin" ? "admin-tokens" : "tokens";
        try {
            const res = await api("GET", `/${path}/${tokenId}/view`);
            setRevealed(prev => ({ ...prev, [key]: { token: res.token } }));
        } catch (e) {
            const error = e.status === 410
                ? "ดู token นี้ไม่ได้ (สร้างก่อนรองรับฟีเจอร์นี้)"
                : e.status === 403
                    ? "ไม่มีสิทธิ์ดู token (403) — ต้องมีสิทธิ์ dds:manage"
                    : `ดู token ไม่สำเร็จ: ${e.message}`;
            setRevealed(prev => ({ ...prev, [key]: { error } }));
        }
    }

    async function createAdminToken(screenId) {
        try {
            const res = await api("POST", "/admin-tokens", { screenId, label: `admin จอ ${screenId}` });
            setNewToken({ screenId, token: res.token, type: "admin" });
            await load();
        } catch (e) { setError(e.message); }
    }

    async function revokeAdminToken(tokenId) {
        if (!window.confirm("ยกเลิก admin token นี้?")) return;
        try { await api("POST", `/admin-tokens/${tokenId}/revoke`); await load(); }
        catch (e) { setError(e.message); }
    }

    async function assign(screenId, contentKey) {
        try {
            await api("POST", "/assignments", { screenId, contentKey, position: 0 });
            await load();
        } catch (e) { setError(e.message); }
    }

    async function removeAssignment(id) {
        try { await api("DELETE", `/assignments/${id}`); await load(); }
        catch (e) { setError(e.message); }
    }

    function updateMediaEvent(screenId, field, value) {
        setMediaEvents(previous => ({
            ...previous,
            [screenId]: {...(previous[screenId] || {url: "", ttlSeconds: 60}), [field]: value}
        }));
    }

    async function triggerMediaEvent(screenId) {
        const event = mediaEvents[screenId] || {};
        const url = String(event.url || "").trim();
        const ttlSeconds = Number(event.ttlSeconds || 60);
        if (!/^https?:\/\//i.test(url)) { setError("Event URL ต้องขึ้นต้นด้วย http:// หรือ https://"); return; }
        if (!Number.isFinite(ttlSeconds) || ttlSeconds < 1 || ttlSeconds > 3600) { setError("Event แสดงได้ 1–3600 วินาที"); return; }
        try {
            await api("POST", "/events/media", {screen_id: screenId, url, ttl_seconds: ttlSeconds});
            setError("");
        } catch (e) { setError(e.message); }
    }

    async function clearMediaEvent(screenId) {
        try { await api("DELETE", `/events/media/${screenId}`); setError(""); }
        catch (e) { setError(e.message); }
    }

    function updateIdleConfig(screenId, field, value) {
        setIdleConfigs(prev => ({
            ...prev,
            [screenId]: { ...(prev[screenId] || { idleMinutes: "", idleShowMinutes: "", idleImageUrl: "", idleCooldownMinutes: "" }), [field]: value }
        }));
    }

    async function saveIdleConfig(screenId) {
        const cfg = idleConfigs[screenId] || {};
        const idleMinutes = cfg.idleMinutes === "" || cfg.idleMinutes == null ? null : Number(cfg.idleMinutes);
        const idleShowMinutes = cfg.idleShowMinutes === "" || cfg.idleShowMinutes == null ? null : Number(cfg.idleShowMinutes);
        const idleCooldownMinutes = cfg.idleCooldownMinutes === "" || cfg.idleCooldownMinutes == null ? null : Number(cfg.idleCooldownMinutes);
        const idleImageUrl = String(cfg.idleImageUrl || "").trim() || null;
        try {
            await api("PUT", `/screens/${screenId}/idle-config`, { idleMinutes, idleShowMinutes, idleImageUrl, idleCooldownMinutes });
            setError("");
            await load();
        } catch (e) { setError(e.message); }
    }

    const MONEY_CARD_DEFAULT_SECONDS = 10;
    const MONEY_CARD_MIN_SECONDS = 3;
    const MONEY_CARD_MAX_SECONDS = 60;

    async function saveMoneyCardSeconds(sc) {
        const typed = moneyCardInputs[sc.id];
        const raw = typed === undefined ? (sc.moneyCardSeconds ?? "") : String(typed).trim();
        let moneyCardSeconds = null;
        if (raw !== "") {
            moneyCardSeconds = Number(raw);
            if (!Number.isInteger(moneyCardSeconds) || moneyCardSeconds < MONEY_CARD_MIN_SECONDS || moneyCardSeconds > MONEY_CARD_MAX_SECONDS) {
                setError(`เวลาการ์ดส่งเงินต้องเป็นจำนวนเต็ม ${MONEY_CARD_MIN_SECONDS}–${MONEY_CARD_MAX_SECONDS} วินาที (หรือเว้นว่างเพื่อใช้ค่าเริ่มต้น)`);
                return;
            }
        }
        try {
            await api("PUT", `/screens/${sc.id}/money-card-config`, { moneyCardSeconds });
            setError("");
            setMoneyCardInputs(prev => { const next = { ...prev }; delete next[sc.id]; return next; });
            await load();
        } catch (e) {
            setError(String(e.message).startsWith("400")
                ? `เวลาการ์ดส่งเงินต้องอยู่ระหว่าง ${MONEY_CARD_MIN_SECONDS}–${MONEY_CARD_MAX_SECONDS} วินาที`
                : e.message);
        }
    }

    async function stopIdleCycle(screenId) {
        try {
            await api("POST", `/screens/${screenId}/idle-config/stop`);
            setError("");
            await load();
        } catch (e) { setError(e.message); }
    }

    const s = {
        page: { padding: "24px", color: "#1f2937", background: "#f4f6f8", fontFamily: "'Segoe UI', sans-serif", maxWidth: 900 },
        h1: { fontSize: 22, fontWeight: 700, marginBottom: 8, color: "#1f2937" },
        section: { background: "#ffffff", borderRadius: 10, padding: 16, marginBottom: 16, border: "1px solid #e2e8f0", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" },
        sh: { fontSize: 14, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 },
        card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 14, marginBottom: 10 },
        label: { fontSize: 13, color: "#6b7280", marginBottom: 4 },
        val: { fontSize: 15, color: "#1f2937", wordBreak: "break-all" },
        input: { padding: "8px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#ffffff", color: "#1f2937", fontSize: 14, marginRight: 8 },
        btn: { padding: "7px 16px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 13, marginRight: 6, fontWeight: 600 },
        btnBlue: { background: "#dc3545", color: "#fff" },
        btnRed: { background: "#dc3545", color: "#fff" },
        btnGray: { background: "#747e8a", color: "#ffffff" },
        tag: { display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: "#dbeafe", color: "#1e40af", marginRight: 4 },
        tagGreen: { background: "#dcfce7", color: "#166534" },
        tagRed: { background: "#fee2e2", color: "#991b1b" },
        err: { background: "#f8d7da", color: "#842029", border: "1px solid #f5c2c7", padding: "10px 14px", borderRadius: 6, marginBottom: 12, fontSize: 13 },
        mono: { fontFamily: "monospace", fontSize: 12, background: "#f1f5f9", border: "1px solid #e2e8f0", padding: "6px 10px", borderRadius: 4, color: "#0f172a", wordBreak: "break-all" },
    };

    function renderTokenRow(kind, t, onRevoke) {
        const key = `${kind}:${t.id}`;
        const r = revealed[key];
        return (
            <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ ...s.tag, ...(t.revokedAt ? s.tagRed : s.tagGreen) }}>
                        {t.revokedAt ? "revoked" : "active"}
                    </span>
                    <span style={{ fontSize: 13, color: "#6b7280" }}>{t.label}</span>
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>สร้างเมื่อ {fmtIct(t.createdAt)}</span>
                    <button style={{ ...s.btn, ...s.btnGray, padding: "3px 10px", fontSize: 11 }}
                        onClick={() => toggleReveal(kind, t.id)}>
                        {r !== undefined ? "ซ่อน" : "แสดง"}
                    </button>
                    {!t.revokedAt && (
                        <button style={{ ...s.btn, ...s.btnRed, padding: "3px 10px", fontSize: 11 }}
                            onClick={() => onRevoke(t.id)}>revoke</button>
                    )}
                </div>
                {r !== undefined && (
                    r.error ? (
                        <div style={{ fontSize: 12, color: "#dc2626" }}>{r.error}</div>
                    ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={s.mono}>{r.token}</div>
                            <button style={{ ...s.btn, ...s.btnGray, padding: "4px 12px", fontSize: 12 }}
                                onClick={() => navigator.clipboard.writeText(r.token).then(() => alert("คัดลอกแล้ว!"))}>
                                คัดลอก
                            </button>
                        </div>
                    )
                )}
            </div>
        );
    }

    const unassignedContents = (screenId) =>
        contents.filter(c => !(assignments[screenId] || []).some(a => a.contentKey === c.key));

    return (
        <div style={s.page}>
            <h1 style={s.h1}>🖥️ DDS Admin — จัดการหน้าจอแสดงผล</h1>
            <p style={{ color: "#64748b", fontSize: 13, marginBottom: 16 }}>
                ข้อมูลจอแสดงผลโรงงาน — dds.icecys.com
            </p>

            {error && <div style={s.err}>⚠️ {error}</div>}
            {loading && <div style={{ color: "#64748b", marginBottom: 12 }}>กำลังโหลด...</div>}

            {/* New token card — fixed overlay so it is visible wherever the page is scrolled */}
            {newToken && (
                <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,0.45)",
                              display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
                     role="dialog" aria-modal="true" data-testid="new-token-card">
                <div style={{ ...s.section, borderColor: "#ca8a04", background: "#fff8e1", marginBottom: 0,
                              maxWidth: 640, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
                    <div style={{ ...s.sh, color: "#92600a" }}>
                        🔑 {newToken.type === "admin" ? "Admin Token" : "Display Token"} ใหม่ ที่เพิ่งสร้าง
                    </div>
                    <p style={{ fontSize: 13, color: "#78350f", marginBottom: 8 }}>
                        คัดลอกเก็บไว้ได้เลย — ปิดแล้วกลับมาดูได้จากปุ่ม "แสดง" ในแถว token (token ที่สร้างหลังรองรับฟีเจอร์นี้)
                    </p>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={s.mono}>{newToken.token}</div>
                        <button style={{ ...s.btn, ...s.btnGray, padding: "4px 12px", fontSize: 12 }}
                            onClick={() => navigator.clipboard.writeText(newToken.token).then(() => alert("คัดลอกแล้ว!"))}>
                            คัดลอก
                        </button>
                    </div>
                    {newToken.type === "display" && (
                        <div style={{ marginTop: 8, fontSize: 12, color: "#78716c" }}>
                            URL จอ: <span style={s.mono}>{DDS_DISPLAY_BASE}/{newToken.screenId}</span>
                        </div>
                    )}
                    {newToken.type === "admin" && (
                        <div style={{ marginTop: 8, fontSize: 12, color: "#78716c" }}>
                            Admin URL: <span style={s.mono}>{DDS_DISPLAY_BASE}/{newToken.screenId}/admin</span>
                        </div>
                    )}
                    <button style={{ ...s.btn, ...s.btnGray, marginTop: 10 }} onClick={() => setNewToken(null)}>ปิด</button>
                </div>
                </div>
            )}

            {/* Create screen */}
            <div style={s.section}>
                <div style={s.sh}>สร้าง Screen ใหม่</div>
                <input style={s.input} placeholder="ชื่อ screen เช่น โรงงาน จอ 1"
                    value={newScreenName} onChange={e => setNewScreenName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && createScreen()} />
                <button style={{ ...s.btn, ...s.btnBlue }} onClick={createScreen}>+ สร้าง</button>
            </div>

            {/* Screens list */}
            {screens.length === 0 && !loading && (
                <div style={{ color: "#475569", fontSize: 14 }}>ยังไม่มี screen — สร้างด้านบน</div>
            )}
            {screens.map(sc => (
                <div key={sc.id} style={s.card}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <div>
                            <span style={{ fontWeight: 700, fontSize: 16 }}>{sc.name}</span>
                            <span style={{ ...s.tag, marginLeft: 8 }}>id={sc.id}</span>
                        </div>
                        <button style={{ ...s.btn, ...s.btnRed, fontSize: 12 }} onClick={() => deleteScreen(sc.id)}>ลบ screen</button>
                    </div>

                    {/* Display URL */}
                    <div style={{ ...s.label, marginBottom: 4 }}>Display URL:</div>
                    <div style={s.mono}>{DDS_DISPLAY_BASE}/{sc.id}</div>

                    {/* Events */}
                    <div style={{ marginTop: 12 }}>
                        <div style={s.sh}>✨ Event — แสดงรูปจาก URL</div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 7 }}>
                            แสดงเต็มจอชั่วคราว แล้วกลับสู่ข้อมูลเดิมอัตโนมัติ
                        </div>
                        <input style={{ ...s.input, width: "min(100%, 480px)" }} type="url" placeholder="https://.../event.png"
                            value={(mediaEvents[sc.id] || {}).url || ""}
                            onChange={e => updateMediaEvent(sc.id, "url", e.target.value)} />
                        <input style={{ ...s.input, width: 90 }} type="number" min="1" max="3600" aria-label="วินาที"
                            value={(mediaEvents[sc.id] || {}).ttlSeconds || 60}
                            onChange={e => updateMediaEvent(sc.id, "ttlSeconds", e.target.value)} />
                        <span style={{ fontSize: 12, color: "#6b7280", marginRight: 8 }}>วินาที</span>
                        <button style={{ ...s.btn, ...s.btnBlue }} onClick={() => triggerMediaEvent(sc.id)}>แสดง</button>
                        <button style={{ ...s.btn, ...s.btnGray }} onClick={() => clearMediaEvent(sc.id)}>หยุด</button>
                    </div>

                    {/* Idle auto-display */}
                    <div style={{ marginTop: 12 }}>
                        <div style={s.sh}>💤 Idle Auto-Display — โชว์รูปอัตโนมัติเมื่อไม่มีขายใหม่</div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 7 }}>
                            ไม่มีการขายใหม่นานเกินกำหนด → โชว์รูปนี้อัตโนมัติ แล้วกลับข้อมูลปกติเอง (มีขายใหม่ระหว่างโชว์ → กลับทันที) หลังโชว์จบจะคูลดาวน์ก่อนโชว์รอบใหม่ได้
                        </div>
                        <input style={{ ...s.input, width: 110 }} type="number" min="1" placeholder="นาทีก่อนโชว์"
                            value={(idleConfigs[sc.id] || {}).idleMinutes ?? ""}
                            onChange={e => updateIdleConfig(sc.id, "idleMinutes", e.target.value)} />
                        <input style={{ ...s.input, width: 110 }} type="number" min="1" placeholder="โชว์กี่นาที"
                            value={(idleConfigs[sc.id] || {}).idleShowMinutes ?? ""}
                            onChange={e => updateIdleConfig(sc.id, "idleShowMinutes", e.target.value)} />
                        <input style={{ ...s.input, width: 150 }} type="number" min="1" placeholder="คูลดาวน์ (นาที)"
                            value={(idleConfigs[sc.id] || {}).idleCooldownMinutes ?? ""}
                            onChange={e => updateIdleConfig(sc.id, "idleCooldownMinutes", e.target.value)} />
                        <input style={{ ...s.input, width: "min(100%, 380px)" }} type="url" placeholder="https://.../idle.png"
                            value={(idleConfigs[sc.id] || {}).idleImageUrl ?? ""}
                            onChange={e => updateIdleConfig(sc.id, "idleImageUrl", e.target.value)} />
                        <button style={{ ...s.btn, ...s.btnBlue }} onClick={() => saveIdleConfig(sc.id)}>บันทึก</button>
                        <button style={{ ...s.btn, ...s.btnGray }} onClick={() => stopIdleCycle(sc.id)}>หยุด</button>
                    </div>

                    {/* Money-submitted card duration */}
                    <div style={{ marginTop: 12 }}>
                        <div style={s.sh}>💵 การ์ด "ส่งเงินแล้ว" — เวลาที่การ์ดค้างบนจอ</div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 7 }}>
                            ตอนนี้: {sc.moneyCardSeconds != null ? `${sc.moneyCardSeconds} วินาที` : `ค่าเริ่มต้น (${MONEY_CARD_DEFAULT_SECONDS})`} · ตั้งได้ {MONEY_CARD_MIN_SECONDS}–{MONEY_CARD_MAX_SECONDS} วินาที · เว้นว่าง = ค่าเริ่มต้น
                        </div>
                        <span style={{ fontSize: 12, color: "#6b7280", marginRight: 8 }}>เวลาการ์ดส่งเงิน (วินาที)</span>
                        <input style={{ ...s.input, width: 150 }} type="number" min={MONEY_CARD_MIN_SECONDS} max={MONEY_CARD_MAX_SECONDS}
                            placeholder={`ค่าเริ่มต้น (${MONEY_CARD_DEFAULT_SECONDS})`}
                            value={moneyCardInputs[sc.id] !== undefined ? moneyCardInputs[sc.id] : (sc.moneyCardSeconds ?? "")}
                            onChange={e => setMoneyCardInputs(prev => ({ ...prev, [sc.id]: e.target.value }))} />
                        <button style={{ ...s.btn, ...s.btnBlue }} onClick={() => saveMoneyCardSeconds(sc)}>บันทึก</button>
                    </div>

                    {/* Display Tokens */}
                    <div style={{ marginTop: 12 }}>
                        <div style={s.sh}>Display Tokens (สำหรับหน้าจอแสดงผล)</div>
                        {(tokens[sc.id] || []).map(t => renderTokenRow("display", t, revokeToken))
                        }
                        <button style={{ ...s.btn, ...s.btnBlue, fontSize: 12, marginTop: 4 }}
                            onClick={() => createToken(sc.id)}>+ สร้าง Display Token</button>
                    </div>

                    {/* Admin Tokens */}
                    <div style={{ marginTop: 12 }}>
                        <div style={s.sh}>Admin Tokens (สำหรับ /{sc.id}/admin)</div>
                        {(adminTokens[sc.id] || []).map(t => renderTokenRow("admin", t, revokeAdminToken))
                        }
                        <button style={{ ...s.btn, ...s.btnBlue, fontSize: 12, marginTop: 4 }}
                            onClick={() => createAdminToken(sc.id)}>+ สร้าง Admin Token</button>
                    </div>

                    {/* Assignments */}
                    <div style={{ marginTop: 12 }}>
                        <div style={s.sh}>Content ที่แสดง</div>
                        {(assignments[sc.id] || []).map(a => (
                            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <span style={s.tag}>{a.contentKey}</span>
                                <span style={{ fontSize: 13, color: "#6b7280" }}>{a.contentKey}</span>
                                <button style={{ ...s.btn, ...s.btnRed, padding: "3px 10px", fontSize: 11 }}
                                    onClick={() => removeAssignment(a.id)}>ลบ</button>
                            </div>
                        ))}
                        {unassignedContents(sc.id).map(c => (
                            <button key={c.key} style={{ ...s.btn, ...s.btnGray, fontSize: 12, marginTop: 4, marginRight: 6 }}
                                onClick={() => assign(sc.id, c.key)}>
                                + {c.title || c.key}
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
